/* eslint-disable max-depth */

// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapActions, mapState } from 'vuex';
import api from '../../../common/api';
import config from '../../../share/config';
import handler from '../../../share/util/apiResultValidator';
import vehicle from '../../../share/util/vehicle';
import testMode from '../../../share/util/testModeRepro';
import reprogram from '../../../share/util/reprogram';
import util from '../../../share/util/utils';
import { EXECUTE_NAVIGATION_RESOURCES } from '../../../constants/navigationResources';
const vehicleTestMode = require('../../../data/testMode/vehicleInfo.json');
const publishedUnitListTestMode = require('../../../data/testMode/publishedUnits.json');

const AGREEMENT_TYPES = {
  USER_AGREEMENT: "useragreement",
  PERSONAL_DATA: "personal_data_agreement"
};

export default {
  data() {
    return {
      processing: false,
      isConfirmed: false,
      useragreement: '',
      agreementType: null
    };
  },

  // ナビゲーションガード
  beforeRouteEnter(_, from, next) {
    // 同意画面の表示は blank からのみ実施する
    if (from.name === 'blank') {
      next();
    }
  },

  beforeRouteUpdate() {
    if (this.isPopstateNavigation) {
      this.cacheIsPopstateNavigation(false);
      return;
    }
    this.setTermsAgreements();
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      userId: state => state.userProperty.userId,
      lastPairingNetAppId: state => state.userProperty.lastPairingNetAppId,
      region: state => state.userProperty.region,
      currentIsAgreement: state => state.userProperty.isAgreement, // Backendから取得した利用規約の同意状態
      currentIsAgreementPersonalData: state => state.userProperty.isAgreementPersonalData, // Backendから取得した個人情報利用の同意状態
      targetUnits: state => state.masterResources.targetUnits,
      isPopstateNavigation: state => state.cache.isPopstateNavigation,
    }),

    isAgreementChecked: {
      get() {
        return this.isConfirmed;
      },
      set(value) {
        this.isConfirmed = value;
      }
    },
    title: {
      get() {
        // ページ上部のタイトルを同意対象によって切り換える
        if (this.agreementType === null) {
          return "";
        }

        const messageKey = {
          [AGREEMENT_TYPES.PERSONAL_DATA]: "message.header_sub_terms_personal_data",
          [AGREEMENT_TYPES.USER_AGREEMENT]: "message.header_sub_terms_agreement"
        };
        return this.$t(messageKey[this.agreementType]);
      }
    },
    checkBoxTitle: {
      get() {
        // チェックボックスのメッセージを同意対象によって切り換える
        if (this.agreementType === null) {
          return "";
        }

        const messageKey = {
          [AGREEMENT_TYPES.PERSONAL_DATA]: "message.title_personal_data_agreement_box",
          [AGREEMENT_TYPES.USER_AGREEMENT]: "message.title_agreement_box"
        };
        return this.$t(messageKey[this.agreementType]);
      }
    }
  },

  watch: {
    language: {
      handler() {
        this.$router.push({ name: 'termsAgreement', query: { language: this.language } });
      }
    },
  },

  methods: {
    ...mapActions('app', ['setIsSkipStart', 'setBreadCrumbs']),
    ...mapActions('auth', ['logout']),
    ...mapActions('netAppConnector', ['getPairingStatus']),
    ...mapActions('cache', ['cacheVehicleInfo', 'cacheIsPopstateNavigation']),
    ...mapActions('targetUnit', ['setTargetUnit']),
    ...mapActions('vehicle', ['setvoltage']),

    goToNext: async function () {

      this.processing = true;
      // 同意対象によってリクエストボディのserviceを切り換える
      const service = this.agreementType === AGREEMENT_TYPES.PERSONAL_DATA ? "personal_data" : config.SERVICE_REPRO;
      const response = await api.postCall(config.ADMIN, '/users/' + this.userId + '/agreement', { service: service });

      const isSuccess = handler.validate(handler.validateTypes.all,
        response, this, null,
        () => { },   // 処理が成功したときのコールバック処理
        (result) => {
          // 処理が失敗したときのコールバック処理
          this.processing = false;
          if (result.behaviorType === handler.behaviorTypes.logout) {
            this.logout(this);
          }
        }
      );

      // validate が成功した場合は処理を続行する。
      if (isSuccess) {
        if (this.agreementType === AGREEMENT_TYPES.PERSONAL_DATA && !this.currentIsAgreement) {
          // 個人情報利用の同意後に利用規約未同意の場合は、同意のチェックを外して利用規約の同意画面を表示する スクロールも一番上に戻す
          this.agreementType = AGREEMENT_TYPES.USER_AGREEMENT;
          this.isConfirmed = false;
          this.setTermsAgreements();
          return;
        }

        // 前回接続済みのクラウドコネクターIDがあるかチェック
        if (this.lastPairingNetAppId) {
          const param = {
            this: this,
            netAppId: this.lastPairingNetAppId
          };
          const result = await this.getPairingStatus(param);
          if (result.isConnected) {
            const pairingStatus = result.response.data;
            // リプロ実行中のチェック
            if (reprogram.isRunningReprogram(pairingStatus.repro_status)) {
              // 自動接続はOFFに設定しておく
              this.setIsSkipStart(false);

              // 車両情報を設定する
              this.cacheVehicleInfo(pairingStatus);

              // 取得した電圧情報のみ vehicle に保存する
              if (pairingStatus.is_test_mode) {
                if (vehicleTestMode.vehicle && vehicleTestMode.vehicle.battery_voltage) {
                  if (vehicleTestMode.vehicle.battery_voltage.is_valid) {
                    this.setvoltage(vehicleTestMode.vehicle.battery_voltage.voltage);
                  }
                }
                pairingStatus.vehicle.unit_id = testMode.testModeSystemNameDisplay(pairingStatus, publishedUnitListTestMode);
              } else {
                if (pairingStatus.vehicle.battery_voltage) {
                  if (pairingStatus.vehicle.battery_voltage.is_valid) {
                    this.setvoltage(pairingStatus.vehicle.battery_voltage.voltage);
                  }
                }
              }

              // 対象ユニット情報を設定する
              const unitInfo = vehicle.createTargetUnitInfo(pairingStatus, true);
              this.setTargetUnit(unitInfo);

              // information-main 配下を保持する
              const infoMainIndex = this.$router.options.routes.findIndex(item => item.name === 'informationMain');
              // information-tabs 配下を保持する
              const infoTabs = this.$router.options.routes[infoMainIndex];
              const infoTabsIndex = infoTabs.children.findIndex(item => item.name === 'informationTabs');
              const infoTabRoutes = infoTabs.children[infoTabsIndex].children;
              // ナビゲーションの追加設定
              // 第三階層のページは、通信車両が SU 法規対象／非対象かで動的に変化する
              const naviList = ['blank', 'home', 'menu', 'systemTable', 'suSystemTable', 'systemFileTable', 'systemInformation'];
              for (let index = 0; index < naviList.length; index++) {
                const name = naviList[index];
                const findIndex = this.$router.options.routes.findIndex(item => item.name === name);
                if (findIndex !== -1) {
                  const routerData = this.$router.options.routes[findIndex];
                  const data = util.createBreadCrumbData(routerData, EXECUTE_NAVIGATION_RESOURCES);
                  this.setBreadCrumbs(data);
                }
                else {
                  // 見つからなかった場合は information-tabs 配下を検索する
                  const secondIndex = infoTabRoutes.findIndex(item => item.name === name);
                  if (secondIndex !== -1) {
                    const routerData = infoTabRoutes[secondIndex];
                    const data = util.createBreadCrumbData(routerData, EXECUTE_NAVIGATION_RESOURCES);
                    this.setBreadCrumbs(data);
                  }
                }
              }

              // リプロ実行中の場合はリプロ実行画面へ遷移
              this.$router.push({ name: 'execution', params: { response: JSON.stringify(pairingStatus) } });
              return;
            }
          }
        }
        // 管理メニュー画面に遷移
        this.$router.push({ name: "home" });
      }

      this.processing = false;
    },

    /**
     * 画面に表示する同意文を更新する
     * @param {string} language 選択中の言語
     * @param {string} agreementType 同意対象の種類（利用規約 or 個人情報の利用）
     */
    updateUserAgreement: async function (language, agreementType) {
      // 利用規約と個人情報利用の同意で取得する文言リソースを切り換える
      const props = {
        [AGREEMENT_TYPES.USER_AGREEMENT]: { service: config.SERVICE_REPRO, textPropName: "user_agreement" },
        [AGREEMENT_TYPES.PERSONAL_DATA]: { service: null, textPropName: "consent" }
      };

      const params = {
        language: language,
        target: agreementType,
        service: props[agreementType].service
      };

      this.processing = true;
      const response = await api.getParamCall(config.ADMIN, '/fileResources', params);
      this.processing = false;

      handler.validate(handler.validateTypes.all,
        response, this, null,
        () => {
          // 処理が成功したときのコールバック処理
          const obj = JSON.parse(response.data.resources);
          const textPropName = props[agreementType].textPropName;
          this.useragreement = obj[textPropName];
        },
        (result) => {
          // 処理が失敗したときのコールバック処理
          if (result.behaviorType === handler.behaviorTypes.logout) {
            this.logout(this);
          }
        }
      );
    },

    /**
     * 同意画面の作成処理
     * 言語変更時や個人情報保護から利用規約に切り替えるときにも画面を更新する
     * スクロールも一番上に戻す
     */
    setTermsAgreements: async function () {
      document.querySelector('.terms-scroll-box').scrollTop = 0;
      if (this.agreementType === null) {
        this.agreementType = this.currentIsAgreementPersonalData ? AGREEMENT_TYPES.USER_AGREEMENT : AGREEMENT_TYPES.PERSONAL_DATA;
      }
      await this.updateUserAgreement(this.language, this.agreementType);
    }
  },

  mounted: async function () {
    await this.setTermsAgreements();
  }
};